import useRemoveShopLogo from "@babackapp/private-api-client/client/use-remove-shop-logo";
import FormEditableAvatar from "@babackapp/react-hook-forms-components/form-editable-avatar";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import { SettingsFormSectionHeader } from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { FormField } from "@babackapp/ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { Label } from "@babackapp/ui/components/label";
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import useUploadShopLogo from "@/api/shop-logo/use-upload-shop-logo";
import useCurrentShopLogoUrl from "@/api/shops/use-current-shop-logo-url";
import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import type { ShopSettingsFormValues } from "./types";
import { OverrideRulesManager } from "@/components/override-rules";
import MarketConversionRatesForm from "./market-conversion-rates-form";
import { LockClosedIcon, SparklesIcon } from "@heroicons/react/24/solid";

export default function ShopSettingsInformationForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { control, resetField } = useFormContext<ShopSettingsFormValues>();

  const { mutateAsync: uploadShopLogo, isPending: uploadPending } =
    useUploadShopLogo();

  const defaultImage = useCurrentShopLogoUrl();
  const removeShopLogo = useRemoveShopLogo({ shopId: shop.id });

  /**
   * If the logo has been updated and the field is dirty,
   * simply reset the field,
   * otherwise, remove the logo from the shop.
   */
  const _onRemoveShopLogo = useCallback(
    (isDirty: boolean) => {
      if (isDirty) return resetField("logoId");
      return removeShopLogo.mutate();
    },
    [removeShopLogo, resetField],
  );

  const isAdmin = useViewerHasAtLeastAdminRights();
  const disabledBecauseNotAdmin = isAdmin
    ? false
    : t("settings.shop.errors.notAdmin");

  const isRetractionOnly = useIsRetractionOnly();

  /**
   * TODO: Placeholder skeleton
   */
  if (!shop) return null;

  return (
    <>
      <SettingsFormSectionHeader
        title={t("settings.shop.sections.basic.title")}
        id="information"
      />
      <Card>
        <CardContent className="py-8 flex flex-col gap-8">
          <FormInputRow>
            <FormField
              control={control}
              name="name"
              disabled={!isAdmin}
              render={({ field }) => (
                <FormInput
                  label={t("misc.name")}
                  placeholder={t("misc.name")}
                  {...field}
                  disabled={disabledBecauseNotAdmin}
                />
              )}
            />

            <FormField
              control={control}
              name="logoId"
              disabled={!isAdmin}
              render={({ field: { value }, fieldState: { isDirty } }) => (
                <FormEditableAvatar
                  label={t("settings.shop.logo")}
                  description={t("settings.shop.logoDescription")}
                  fallback={shop.name}
                  initialValue={value?.url ?? (defaultImage as any)?.data ?? ""}
                  disabled={disabledBecauseNotAdmin}
                  onRemove={() => _onRemoveShopLogo(isDirty)}
                  isLoading={removeShopLogo.isPending || uploadPending}
                  onUpload={uploadShopLogo}
                />
              )}
            />
          </FormInputRow>

          <FormField
            control={control}
            name="vatNumber"
            disabled={!isAdmin}
            render={({ field }) => (
              <FormInput
                label={t("settings.shop.vat")}
                description={t("settings.shop.vatDescription")}
                {...field}
                placeholder={t("settings.shop.vat")}
                disabled={disabledBecauseNotAdmin}
              />
            )}
          />
          <div>
            {isRetractionOnly ? (
              <div className="space-y-4">
                {/* Délai fixe 14 jours */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    {t("settings.shop.timeDelay.title")}
                  </p>
                  <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                    <span className="text-sm font-medium">14</span>
                    <span className="text-xs text-muted-foreground">
                      — Délai légal de rétractation (14 jours), non modifiable
                    </span>
                  </div>
                </div>

                {/* Mock floutté des règles d'exception + CTA */}
                <div className="relative mt-2 overflow-hidden rounded-lg border">
                  {/* Mock statique des règles */}
                  <div className="pointer-events-none select-none p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        Règles d&apos;exception sur le délai
                      </span>
                      <div className="h-7 w-7 rounded border bg-muted" />
                    </div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Règles actives
                    </div>
                    {[
                      {
                        priority: 1,
                        name: "Prolongation été",
                        desc: "Délai étendu à 30 jours en août",
                      },
                      {
                        priority: 2,
                        name: "Soldes hiver",
                        desc: "Retour sous 7 jours uniquement",
                      },
                    ].map((rule) => (
                      <div
                        key={rule.priority}
                        className="flex items-center gap-3 rounded-md border bg-background px-3 py-2 text-xs"
                      >
                        <span className="font-semibold text-muted-foreground w-4 text-right">
                          {rule.priority}
                        </span>
                        <span className="font-medium">{rule.name}</span>
                        <span className="text-muted-foreground">
                          {rule.desc}
                        </span>
                      </div>
                    ))}
                    <div className="h-7 w-36 rounded-md border bg-muted" />
                  </div>

                  {/* Overlay CTA */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100">
                      <SparklesIcon className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Personnalisez votre délai de retour
                      </p>
                      <p className="text-xs text-muted-foreground max-w-xs">
                        Créez des règles d&apos;exception pour adapter le délai
                        selon la période, la catégorie ou le client.
                      </p>
                    </div>
                    <Link
                      to={`/shops/${shop.id}/settings/billing`}
                      className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm"
                    >
                      <LockClosedIcon className="h-3 w-3" />
                      Passer au plan Lite
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <FormField
                  control={control}
                  name="timeDelay"
                  disabled={!isAdmin}
                  render={({ field }) => (
                    <FormInput
                      label={t("settings.shop.timeDelay.title")}
                      type="number"
                      description={t("settings.shop.timeDelay.description")}
                      placeholder={t("settings.shop.timeDelay.title")}
                      disabled={disabledBecauseNotAdmin}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  )}
                />
                <OverrideRulesManager
                  actionType={"OVERRIDE_RETURN_DELAY"}
                  excludeFields={[
                    "returnItemCount",
                    "returnItemResolutions",
                    "returnReasonIds",
                  ]}
                />
              </>
            )}
          </div>
          {isRetractionOnly ? (
            <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
              {/* Mock statique des options avancées */}
              <div className="pointer-events-none select-none p-6 space-y-4">
                {/* Délai de disponibilité de l'étiquette */}
                <div className="space-y-1.5">
                  <div className="text-sm font-medium">
                    {t("settings.shop.returnLabelDownloadDelayDays.title")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t(
                      "settings.shop.returnLabelDownloadDelayDays.description",
                    )}
                  </div>
                  <div className="h-9 w-24 rounded-md border bg-muted/50 flex items-center px-3">
                    <span className="text-sm text-muted-foreground">30</span>
                  </div>
                </div>
                {/* Annulation automatique */}
                <div className="space-y-1.5">
                  <div className="text-sm font-medium">
                    {t("settings.shop.returnCancellation.title")}
                  </div>
                  <div className="h-9 w-36 rounded-md border bg-muted/50 flex items-center px-3">
                    <span className="text-sm text-muted-foreground">
                      {t("settings.shop.returnCancellation.options.never")}
                    </span>
                  </div>
                </div>
                {/* Gift Codes */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">
                      {t("settings.shop.giftCode.title")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("settings.shop.giftCode.description")}
                    </div>
                  </div>
                  <div className="h-5 w-9 rounded-full bg-muted border" />
                </div>
              </div>

              {/* Overlay sans CTA */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
                <p className="text-sm font-semibold text-foreground">
                  Options de configuration avancées
                </p>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Délai de disponibilité de l&apos;étiquette, annulation
                  automatique des retours et codes cadeaux — disponibles dans le
                  plan Lite.
                </p>
              </div>
            </div>
          ) : (
            <>
              <FormField
                control={control}
                name="returnLabelDownloadDelayDays"
                disabled={!isAdmin}
                render={({ field }) => (
                  <FormInput
                    label={t(
                      "settings.shop.returnLabelDownloadDelayDays.title",
                    )}
                    type="number"
                    description={t(
                      "settings.shop.returnLabelDownloadDelayDays.description",
                    )}
                    placeholder={t(
                      "settings.shop.returnLabelDownloadDelayDays.title",
                    )}
                    disabled={disabledBecauseNotAdmin}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                )}
              />
              <FormField
                control={control}
                name="returnCancellationDelayMonths"
                disabled={!isAdmin}
                render={({ field }) => (
                  <div className="space-y-2">
                    <Label>{t("settings.shop.returnCancellation.title")}</Label>
                    <Select
                      value={String(field.value)}
                      onValueChange={(value) => field.onChange(Number(value))}
                      disabled={!!disabledBecauseNotAdmin}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-1">
                          {t("settings.shop.returnCancellation.options.never")}
                        </SelectItem>
                        <SelectItem value="1">
                          {t(
                            "settings.shop.returnCancellation.options.oneMonth",
                          )}
                        </SelectItem>
                        <SelectItem value="2">
                          {t(
                            "settings.shop.returnCancellation.options.twoMonths",
                          )}
                        </SelectItem>
                        <SelectItem value="3">
                          {t(
                            "settings.shop.returnCancellation.options.threeMonths",
                          )}
                        </SelectItem>
                        <SelectItem value="6">
                          {t(
                            "settings.shop.returnCancellation.options.sixMonths",
                          )}
                        </SelectItem>
                        <SelectItem value="12">
                          {t(
                            "settings.shop.returnCancellation.options.oneYear",
                          )}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.shop.returnCancellation.description")}
                    </p>
                  </div>
                )}
              />
              <MarketConversionRatesForm />
              <FormInputRow>
                <FormField
                  name="giftCode.activated"
                  render={({ field }) => (
                    <FormSwitch
                      label={t("settings.shop.giftCode.title")}
                      description={t("settings.shop.giftCode.description")}
                      {...field}
                    />
                  )}
                />
              </FormInputRow>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
